/**
 * Briefing das 8h (spec 2026-10-08): resumo primeiro (placar e top 5), depois o que mudou, preço × mercado por SKU,
 * prontidão do próximo container, oportunidades, envio da exportação do Shopping de Preços e conversa com o Claude.
 * Todo número vem do serviço (`core/briefing.py`); o Claude só interpreta, e a resposta vem com o selo "interpretação da IA".
 * Confirmado = vendas, estoque, preços e cupons (APIs e margens). Estimado = mercado 2026 (faixas do ML).
 */
import { Fragment, useEffect, useRef, useState } from 'react'
import {
  Alert, Box, Button, Card, CardContent, Chip, Collapse, Link, MenuItem, Stack, Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  TextField, Typography,
} from '@mui/material'
import { KpiCard } from '../../components/KpiCard'
import { Seta } from './componentes'
import { LinksConcorrentes } from './LinksConcorrentes'
import { carregarBriefing, enviarShopping, ErroServico, lerNomeExportacao, perguntarBriefing } from './fonte'
import { brl, brl2, CATEGORIAS, dataBr, num, pct } from './logic'
import type { AnuncioPreco, Briefing, LinhaPreco, MsgChat } from './types'

type Cor = 'success' | 'warning' | 'error' | 'info' | 'default' | 'primary'

const LOJA: Record<string, string> = { ml_matriz: 'ML Matriz', ml_extrema: 'ML Extrema', shopee_loja1: 'Shopee Matriz', shopee_loja2: 'Shopee Decora', shein: 'Shein', tiktok: 'TikTok Shop', amazon: 'Amazon' }
const nomeLoja = (k: string) => LOJA[k] ?? k
const MERCADO_CAT: Record<string, string> = { 'Árvores de Natal': 'árvores', 'Pisca Pisca': 'pisca' }
const GRUPOS_MUDANCA: [string, string][] = [
  ['vendas', 'Vendas'], ['preco', 'Preços que mudaram'], ['catalogo', 'Catálogos'], ['invasao', 'Catálogos invadidos'], ['anuncio', 'Anúncios'],
  ['status', 'Status do ritmo'], ['estoque', 'Estoque'], ['cupom', 'Cupons'],
]
const POS: Record<string, [string, Cor]> = { acima: ['▲ acima', 'error'], 'na faixa': ['● na faixa', 'success'], abaixo: ['▼ abaixo', 'info'] }
const SELO: Record<string, [string, Cor]> = {
  ativo: ['no ar', 'success'], pausado: ['pausado', 'warning'], falta: ['sem anúncio', 'error'], chega: ['sem anúncio', 'error'],
  outro: ['outro status', 'default'], vende: ['vende', 'info'],
}
const VEREDITO: Record<string, string> = {
  nosso_outro_anuncio: 'perdendo para outro anúncio nosso', enviar_full: 'vencedor tem Full: enviar ao Full', nao_compensa: 'ganhar não compensa a MC', manter: 'manter',
}
const SUGESTOES = ['Por que o ML não vende árvore?', 'O que fazer antes do container de 14/10?', 'Resuma o dia em 3 linhas']
const linkMl = (id: string) => `https://produto.mercadolivre.com.br/${id.replace('MLB', 'MLB-')}`
const linkCatalogoMl = (id: string) => `https://www.mercadolivre.com.br/p/${id}`
const pctSinal = (v: number) => `${v > 0 ? '+' : ''}${pct(v)}`

function Ext({ href, children }: { href: string; children: React.ReactNode }) {
  return <Link href={href} target="_blank" rel="noopener noreferrer" underline="hover" onClick={(e) => e.stopPropagation()}>{children}</Link>
}

function BotaoCopiar({ codigo }: { codigo: string }) {
  const [txt, setTxt] = useState('copiar')
  return (
    <Button size="small" variant="outlined" sx={{ py: 0, minWidth: 0, ml: 0.5 }} title="A API da Shein não traz o endereço da página: copie o código e procure no site"
      onClick={(e) => {
        e.stopPropagation()
        // clipboard só dentro do clique; sem permissão o código fica visível para selecionar
        const p = navigator.clipboard ? navigator.clipboard.writeText(codigo) : Promise.reject(new Error('sem clipboard'))
        p.then(() => setTxt('copiado')).catch(() => setTxt('selecione o código'))
      }}>{txt}</Button>
  )
}

function LinkAnuncio({ a }: { a: AnuncioPreco }) {
  if (a.loja === 'shein') return <>{a.link ? <Ext href={a.link}>{a.id}</Ext> : a.id}<BotaoCopiar codigo={a.id} /></>
  const href = a.link || (a.loja.startsWith('ml') ? linkMl(a.id) : null)
  return href ? <Ext href={href}>{a.id}</Ext> : <>{a.id}</>
}

/** Os 5 parágrafos de fonte do quadro de preço (texto do mockup v6; período e data da coleta vêm do briefing). */
function rodapeMercado(b: Briefing): [string, string][] {
  const sem = b.placar.mercado_semana
  const per = sem ? Object.values(sem)[0] : null
  const periodo = per ? `${dataBr(per.inicio)} a ${dataBr(per.fim)}/${per.fim.slice(0, 4)}` : 'o período da exportação enviada'
  const emLinks = b.decisao.find((r) => r.links)?.links?.em
  return [
    ['As 3 fontes de mercado', `(1) Shopping: Shopping de Preços (app.shoppingdeprecos.com.br) › Resumo de Vendas › Busca por Categoria, só Mercado Livre, todos os vendedores, sem agrupamento, ${periodo}, Árvores de Natal (MLB117806) e Pisca Pisca (MLB189949); mostra p25 · mediana · p75. (2) Links: concorrentes que você escolheu na planilha de containers por venderem muito, coletados pelo script (API do ML e Chrome). Kit ou pack vira preço por peça × a nossa unidade de venda; link com medida diferente da nossa sai da mediana e aparece riscado no detalhe. Mostra a mediana, a data da coleta e quantos ficaram fora. (3) Catálogos: catálogos de outras marcas que o painel achou comparáveis ao nosso SKU (ficha × ficha, aderência alta ou média), com o preço do vendedor que está ganhando, lido pela API do ML de hora em hora; mostra a mediana e quantos catálogos têm preço agora.`],
    ['Qual fonte define a posição', 'A linha destacada é a usada: Shopping quando o recorte é confiável (volume real vendido na semana); senão Links, se a coleta tem até 7 dias (seus concorrentes de maior venda); senão Catálogos (preço de hoje). Shopping usa a faixa p25–p75; Links e Catálogos usam ±10% da mediana.'],
    ['Como o recorte é feito', 'Árvores: mesma altura (ex.: 1,80 m = 170 a 190 cm) e número de galhos até 25% acima ou abaixo do nosso, lido do título; nevadas, coloridas, mini e kits ficam de fora. Iluminação: mesmo grupo (tipo, comprimento e nº de LEDs), lido do título. Anúncio cujo título não informa a medida fica fora do recorte.'],
    ['Como os números são calculados', 'Quantidade e faturamento vêm em faixas do ML (ex.: "+20" = de 20 a 49 unidades) e são lidos pelo mínimo da faixa, igual à base de 2025. p25, mediana e p75 são ponderados pelas unidades vendidas: na mediana, metade das unidades vendeu mais barato e metade mais caro. A base é só do Mercado Livre; Shopee e Shein não têm uma base equivalente.'],
    ['Nosso preço e MC', 'Preço de venda e cupons lidos pela API de cada loja na montagem (ML: promoções e cupons do vendedor; Shopee: cupons da loja; Shein: sem leitura de cupom). MC = margem antes de ads, com comissão, frete e imposto do anúncio. "MC na mediana" = MC se o anúncio vendesse pela mediana do mercado. Piso = preço com MC 0%.'],
    ['Confiança', `Shopping: média (faixas do ML e títulos sem medida). Catálogos: média (preço real de hoje, mas só catálogos e poucos por SKU). Links: alta nos SKUs que têm links (concorrentes de maior venda${emLinks ? `, coleta de ${dataBr(emLinks)}` : ''}). Nosso preço, cupom e MC: alta no ML e na Shopee; na Shein, sem cupom.`],
  ]
}

function CelulaLoja({ r, loja }: { r: LinhaPreco; loja: string }) {
  const m = r.melhor[loja]
  if (!m) return <Typography variant="caption" color="text.secondary">sem anúncio</Typography>
  return (
    <Box component="span" sx={{ fontWeight: r.ref_loja === loja ? 700 : 400 }}>
      {brl2(m.com_cupom)}{!m.cupom_lido ? <sup>1</sup> : m.tem_cupom ? <sup>*</sup> : null}
    </Box>
  )
}

function FonteLinha({ rot, usada, valor, sub }: { rot: string; usada: boolean; valor: React.ReactNode; sub?: string | null }) {
  return (
    <Box sx={{ display: 'grid', gridTemplateColumns: '70px auto 1fr', gap: 0.75, alignItems: 'baseline', px: 0.5, borderRadius: 0.5, bgcolor: usada ? 'action.selected' : undefined }}>
      <Typography variant="caption" color={usada ? 'primary' : 'text.secondary'} sx={{ fontWeight: usada ? 700 : 400 }}>{rot}</Typography>
      <Typography variant="body2">{valor ?? '—'}</Typography>
      <Typography variant="caption" color="text.secondary" noWrap>{sub}</Typography>
    </Box>
  )
}

function DetalheSku({ r }: { r: LinhaPreco }) {
  return (
    <Box sx={{ py: 1.5 }}>
      <TableContainer sx={{ border: 1, borderColor: 'divider', borderRadius: 1 }}>
        <Table size="small">
          <TableHead><TableRow>
            {['Loja', 'Anúncio', 'Preço de venda', 'Cupom', 'Com cupom', 'MC com cupom', 'Visitas 7d', 'Vendas 7d', 'Catálogo'].map((h, i) => (
              <TableCell key={h} align={i >= 2 && i !== 3 && i !== 8 ? 'right' : 'left'} sx={{ whiteSpace: 'nowrap', fontWeight: 600 }}>{h}</TableCell>
            ))}
          </TableRow></TableHead>
          <TableBody>
            {r.anuncios.map((a) => (
              <TableRow key={`${a.loja}|${a.id}`}>
                <TableCell>{nomeLoja(a.loja)}</TableCell>
                <TableCell sx={{ whiteSpace: 'nowrap' }}>
                  <LinkAnuncio a={a} />{a.fora_temporada && <Chip size="small" color="warning" label="fora do cupom da temporada" sx={{ ml: 1 }} />}
                </TableCell>
                <TableCell align="right">{brl2(a.preco)}</TableCell>
                <TableCell>
                  {a.cupom === 'sem leitura' ? <Chip size="small" variant="outlined" label="sem leitura" />
                    : a.desconto ? <Typography variant="caption">{a.cupom}</Typography> : <Typography variant="caption" color="text.secondary">sem cupom</Typography>}
                </TableCell>
                <TableCell align="right">{brl2(a.com_cupom)}</TableCell>
                <TableCell align="right">{a.mc_com_cupom != null ? pct(a.mc_com_cupom, 1) : '—'}</TableCell>
                <TableCell align="right">{a.loja.startsWith('ml') ? num(a.visitas) : '—'}</TableCell>
                <TableCell align="right">{num(a.pc7)}</TableCell>
                <TableCell>{a.cat ? `${a.cat === 'winning' ? 'ganhando' : 'disputando'}${a.p2w && a.cat !== 'winning' ? ` · para ganhar ${brl2(a.p2w)}` : ''}` : '—'}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      {r.links && (
        <Typography variant="caption" component="div" sx={{ mt: 1 }}>
          <b>Links de concorrentes (sua planilha, coleta de {dataBr(r.links.em)}):</b>{' '}
          {r.links.ok.map((c, i) => (
            <Fragment key={`${c.link}-${i}`}>{i > 0 && ' · '}<Ext href={c.link}>{brl2(c.preco)}</Ext>
              {c.vendidos && <Typography component="span" variant="caption" color="text.secondary"> ({c.vendidos})</Typography>}
              {c.motivo && <Typography component="span" variant="caption" color="text.secondary"> [{c.motivo}]</Typography>}</Fragment>
          ))}
          {r.links.fora.map((c, i) => (
            <Fragment key={`${c.link}-f${i}`}>{' · '}<Box component="span" sx={{ textDecoration: 'line-through' }}><Ext href={c.link}>{c.preco != null ? brl2(c.preco) : 'sem preço'}</Ext></Box>
              <Typography component="span" variant="caption" color="text.secondary"> fora: {c.motivo}</Typography></Fragment>
          ))}
        </Typography>
      )}
      {r.cat_vivo && (
        <Typography variant="caption" component="div" sx={{ mt: 1 }}>
          <b>Catálogos de terceiros comparáveis (ao vivo):</b>{' '}
          {r.cat_vivo.itens.map((c, i) => (
            <Fragment key={`${c.link}-${i}`}>{i > 0 && ' · '}<Ext href={c.link}>{brl2(c.preco)}{c.full ? ' Full' : ''}</Ext>
              <Typography component="span" variant="caption" color="text.secondary"> ({c.aderencia})</Typography></Fragment>
          ))}
        </Typography>
      )}
    </Box>
  )
}

function LinhaSku({ r, aberto, onAlternar }: { r: LinhaPreco; aberto: boolean; onAlternar: () => void }) {
  const v7 = Object.entries(r.v7).filter(([, q]) => q > 0).map(([l, q]) => `${nomeLoja(l)} ${num(q)}`)
  const sh = r.mercado
  return (
    <>
      <TableRow hover onClick={onAlternar} sx={{ cursor: 'pointer', '& > td': { verticalAlign: 'top' } }}>
        <TableCell><Stack direction="row" spacing={0.5} alignItems="center"><Seta aberta={aberto} /><Box><b>{r.sku}</b>
          <Typography variant="caption" color="text.secondary" display="block" sx={{ maxWidth: 200 }} noWrap title={r.desc}>{r.desc}</Typography></Box></Stack></TableCell>
        <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>{num(r.estoque)} pç<Typography variant="caption" color="text.secondary" display="block">{brl(r.estoque_rs)}</Typography></TableCell>
        <TableCell sx={{ whiteSpace: 'nowrap' }}>{v7.length ? v7.join(' · ') : 'nenhuma'}</TableCell>
        {['ml_matriz', 'shopee_loja1', 'shopee_loja2', 'shein'].map((l) => <TableCell key={l} align="right" sx={{ whiteSpace: 'nowrap' }}><CelulaLoja r={r} loja={l} /></TableCell>)}
        <TableCell>
          <Box sx={{ minWidth: 230 }}>
            <FonteLinha rot="Shopping" usada={r.base_ref === 'shopping'}
              valor={sh ? <>{brl(sh.p25)} · <b>{brl2(sh.mediana)}</b> · {brl(sh.p75)}</> : null}
              sub={sh ? `${r.conf_baixa ? 'fraca · ' : ''}${num(sh.un)} un.` : null} />
            <FonteLinha rot="Links" usada={r.base_ref === 'links'} valor={r.links ? <b>{brl2(r.links.mediana)}</b> : null}
              sub={r.links ? `${r.links.n} · ${dataBr(r.links.em)}${r.links.fora.length ? ` · ${r.links.fora.length} fora` : ''}` : null} />
            <FonteLinha rot="Catálogos" usada={r.base_ref === 'catalogos'} valor={r.cat_vivo ? <b>{brl2(r.cat_vivo.mediana)}</b> : null}
              sub={r.cat_vivo ? `${r.cat_vivo.n} ao vivo` : null} />
          </Box>
        </TableCell>
        <TableCell sx={{ whiteSpace: 'nowrap' }}>
          {r.conf_baixa ? <Chip size="small" variant="outlined" label="referência fraca" />
            : r.pos ? <Chip size="small" color={POS[r.pos][1]} label={`${POS[r.pos][0]}${r.dif != null ? ` ${pctSinal(r.dif)}` : ''}`} /> : '—'}
        </TableCell>
        <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>{r.mc_ref != null ? pct(r.mc_ref, 1) : '—'}
          {r.piso != null && <Typography variant="caption" color="text.secondary" display="block">piso: {brl(r.piso)}</Typography>}</TableCell>
        <TableCell align="right">
          {r.mc_mediana != null && !r.conf_baixa
            ? <Chip size="small" color={r.mc_mediana < 0 ? 'error' : r.mc_mediana < 0.05 ? 'warning' : 'success'} label={pct(r.mc_mediana, 1)} /> : '—'}
        </TableCell>
        <TableCell><Typography variant="body2" sx={{ minWidth: 240, maxWidth: 380 }}>{r.leitura}</Typography></TableCell>
      </TableRow>
      <TableRow><TableCell colSpan={12} sx={{ py: 0, borderBottom: aberto ? undefined : 0 }}>
        <Collapse in={aberto} unmountOnExit><DetalheSku r={r} /></Collapse>
      </TableCell></TableRow>
    </>
  )
}

function Secao({ titulo, sub, direita, children }: { titulo: string; sub?: string; direita?: React.ReactNode; children: React.ReactNode }) {
  return (
    <Card variant="outlined" sx={{ minWidth: 0 }}><CardContent>
      <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={1} flexWrap="wrap" useFlexGap sx={{ mb: 1.5 }}>
        <Box><Typography variant="h6">{titulo}</Typography>{sub && <Typography variant="caption" color="text.secondary">{sub}</Typography>}</Box>
        {direita}
      </Stack>
      {children}
    </CardContent></Card>
  )
}

function ItemLista({ topo, base }: { topo: React.ReactNode; base?: React.ReactNode }) {
  return <Box sx={{ py: 0.75, borderTop: 1, borderColor: 'divider' }}><Typography variant="body2" component="div">{topo}</Typography>
    {base && <Typography variant="caption" color="text.secondary" component="div">{base}</Typography>}</Box>
}

function EnvioShopping({ onMsg }: { onMsg: (m: string) => void }) {
  const [arquivo, setArquivo] = useState<File | null>(null)
  const [categoria, setCategoria] = useState<'arvore' | 'pisca' | ''>('')
  const [inicio, setInicio] = useState('')
  const [fim, setFim] = useState('')
  const [enviando, setEnviando] = useState(false)
  const [erro, setErro] = useState<string | null>(null)
  const entrada = useRef<HTMLInputElement>(null)
  const escolher = (f: File | null) => {
    setArquivo(f); setErro(null)
    if (!f) return
    const l = lerNomeExportacao(f.name)
    setCategoria(l.categoria ?? ''); setInicio(l.inicio); setFim(l.fim)
  }
  const pronto = !!arquivo && !!categoria && !!inicio && !!fim
  const enviar = async () => {
    if (!arquivo || !categoria) return
    setEnviando(true); setErro(null)
    try {
      const r = await enviarShopping(arquivo, categoria, inicio, fim)
      onMsg(`Exportação recebida: ${num(r.linhas)} linhas, ${num(r.unidades)} unidades, ${num(r.skus_com_recorte)} SKUs com recorte. O painel recalcula em segundo plano e atualiza o briefing de hoje em até alguns minutos (a foto das 08:00 não muda).`)
      escolher(null)
      if (entrada.current) entrada.current.value = ''
    } catch (e) { setErro(e instanceof Error ? e.message : String(e)) } finally { setEnviando(false) }
  }
  return (
    <Secao titulo="Enviar exportação do Shopping de Preços"
      sub="Resumo de Vendas › Busca por Categoria, só Mercado Livre, todos os vendedores, sem agrupamento. Um arquivo .xlsx por categoria (árvores e pisca-pisca). Fonte: Shopping de Preços.">
      <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5} alignItems={{ md: 'center' }}>
        <Button variant="outlined" component="label">{arquivo ? arquivo.name : 'Escolher arquivo .xlsx'}
          <input ref={entrada} hidden type="file" accept=".xlsx" onChange={(e) => escolher(e.target.files?.[0] ?? null)} /></Button>
        <TextField select size="small" label="Categoria" value={categoria} onChange={(e) => setCategoria(e.target.value as 'arvore' | 'pisca')} sx={{ minWidth: 160 }}>
          <MenuItem value="arvore">Árvores de Natal</MenuItem><MenuItem value="pisca">Pisca Pisca</MenuItem>
        </TextField>
        <TextField size="small" type="date" label="De" value={inicio} onChange={(e) => setInicio(e.target.value)} slotProps={{ inputLabel: { shrink: true } }} />
        <TextField size="small" type="date" label="Até" value={fim} onChange={(e) => setFim(e.target.value)} slotProps={{ inputLabel: { shrink: true } }} />
        <Button variant="contained" disabled={!pronto || enviando} onClick={() => void enviar()}>{enviando ? 'Enviando…' : 'Enviar'}</Button>
      </Stack>
      {erro && <Alert severity="error" sx={{ mt: 1.5 }}>{erro}</Alert>}
    </Secao>
  )
}

function Conversa() {
  const [msgs, setMsgs] = useState<MsgChat[]>([])
  const [txt, setTxt] = useState('')
  const [carregando, setCarregando] = useState(false)
  const [indisponivel, setIndisponivel] = useState<string | null>(null)
  const [erro, setErro] = useState<string | null>(null)
  const perguntar = async () => {
    const pergunta = txt.trim()
    if (!pergunta || carregando) return
    const hist = msgs
    setMsgs([...hist, { role: 'user', content: pergunta }]); setTxt(''); setCarregando(true); setErro(null)
    try {
      const r = await perguntarBriefing(pergunta, hist)
      setMsgs((m) => [...m, { role: 'assistant', content: r.resposta }])
    } catch (e) {
      setMsgs(hist); setTxt(pergunta)   // pergunta sem resposta sai do histórico (a API exige turnos alternados) e volta para o campo
      if (e instanceof ErroServico && e.status === 503) setIndisponivel(e.message)
      else setErro(e instanceof Error ? e.message : String(e))
    } finally { setCarregando(false) }
  }
  return (
    <Secao titulo="Perguntar ao Claude"
      sub="O Claude Haiku lê os números deste briefing. Interpreta e explica; não faz conta para decisão e não aprova nada."
      direita={<Chip size="small" variant="outlined" label="cota mensal do painel" />}>
      {indisponivel && <Alert severity="info" sx={{ mb: 1.5 }}>Conversa indisponível agora: {indisponivel}</Alert>}
      <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mb: 1.5 }}>
        {SUGESTOES.map((s) => <Button key={s} size="small" variant="outlined" disabled={!!indisponivel} onClick={() => setTxt(s)}>{s}</Button>)}
      </Stack>
      <Stack spacing={1} sx={{ mb: 1.5 }}>
        {msgs.map((m, i) => (
          <Box key={i} sx={{ alignSelf: m.role === 'user' ? 'flex-end' : 'flex-start', maxWidth: '90%', px: 1.5, py: 1, borderRadius: 2, border: 1, borderColor: 'divider',
            bgcolor: m.role === 'user' ? 'action.hover' : 'background.paper' }}>
            {m.role === 'assistant' && <Typography variant="caption" color="primary" display="block" sx={{ fontWeight: 600 }}>interpretação da IA</Typography>}
            <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>{m.content}</Typography>
          </Box>
        ))}
        {carregando && <Typography variant="caption" color="text.secondary">Claude está lendo o briefing…</Typography>}
      </Stack>
      {erro && <Alert severity="warning" sx={{ mb: 1.5 }}>Não foi possível perguntar agora: {erro}</Alert>}
      <Stack direction="row" spacing={1} component="form" onSubmit={(e: React.FormEvent) => { e.preventDefault(); void perguntar() }}>
        <TextField fullWidth size="small" value={txt} onChange={(e) => setTxt(e.target.value)} disabled={!!indisponivel}
          placeholder={indisponivel ? 'Conversa indisponível' : 'Pergunte sobre o briefing de hoje…'} slotProps={{ htmlInput: { 'aria-label': 'Pergunta' } }} />
        <Button type="submit" variant="contained" disabled={!!indisponivel || carregando || !txt.trim()}>Perguntar</Button>
      </Stack>
    </Secao>
  )
}

export function AbaBriefing({ onAbrirAba, onMsg }: { onAbrirAba: (nome: string) => void; onMsg: (m: string) => void }) {
  const [b, setB] = useState<Briefing | null>(null)
  const [erro, setErro] = useState<string | null>(null)
  const [abertos, setAbertos] = useState<Set<string>>(new Set())
  useEffect(() => {
    let vivo = true
    carregarBriefing().then((x) => { if (vivo) setB(x) }).catch((e) => { if (vivo) setErro(e instanceof Error ? e.message : String(e)) })
    return () => { vivo = false }
  }, [])
  const alternar = (k: string) => setAbertos((a) => { const n = new Set(a); if (n.has(k)) n.delete(k); else n.add(k); return n })

  if (erro) return <Alert severity="warning">Briefing ainda não disponível: {erro}</Alert>
  if (!b) return <Typography variant="body2" color="text.secondary" sx={{ py: 3 }}>Carregando o briefing…</Typography>

  const p = b.placar
  const dia = new Date(`${b.dia}T12:00:00`).toLocaleDateString('pt-BR', { weekday: 'long' })
  const lojas = Object.entries(p.por_loja).filter(([, v]) => v > 0).map(([k, v]) => `${nomeLoja(k)} ${num(v)} pç`).join(' · ')
  const metaTotal = Object.values(p.meta_rs_categoria).reduce((a, v) => a + v, 0)
  const sem = p.mercado_semana ? Object.entries(p.mercado_semana).filter(([, s]) => s.un25) : []
  const semPer = p.mercado_semana ? Object.values(p.mercado_semana)[0] : null
  const o = b.oportunidades

  return (
    <Stack spacing={2.5}>
      <Card variant="outlined"><CardContent>
        <Stack direction="row" justifyContent="space-between" alignItems="flex-start" flexWrap="wrap" useFlexGap spacing={1} sx={{ mb: 1.5 }}>
          <Box>
            <Typography variant="h6">Briefing de {dia}, {dataBr(b.dia)}</Typography>
            <Typography variant="caption" color="text.secondary">
              {p.dias_restantes != null ? `Faltam ${p.dias_restantes} dias para 10/12 · ` : ''}vendas e MC oficiais do sistema (margens) · estoque Tiny e APIs dos marketplaces
            </Typography>
          </Box>
          {b.provisorio
            ? <Chip size="small" color="warning" label="provisório: montado agora" />
            : <Chip size="small" variant="outlined" label="Foto das 08:00 · não muda ao longo do dia" />}
        </Stack>
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} flexWrap="wrap" useFlexGap>
          <KpiCard label="Vendido × meta até hoje" value={`${num(p.vendido)} de ${num(p.meta_acum)} pç`}
            hint={`${p.meta_acum > 0 ? pct(p.vendido / p.meta_acum, p.vendido / p.meta_acum < 0.01 ? 2 : 1) : '—'} da meta acumulada`} />
          <KpiCard label="Faturado · MC oficial" value={brl(p.fat)} hint={`MC ${brl(p.mc)} (${p.fat > 0 ? pct(p.mc / p.fat, 1) : '—'})`} />
          <KpiCard label="Vendas por loja" value={lojas || '—'} hint="peças, margens por pedido" />
          {semPer && (
            <KpiCard label={`Mercado ML · ${dataBr(semPer.inicio)} a ${dataBr(semPer.fim)}${sem.length > 0 ? ' × 2025' : ''}`}
              value={sem.length > 0
                ? sem.map(([c, s]) => `${MERCADO_CAT[c] ?? c} ${pctSinal((s.un26 / (s.un25 as number)) - 1)}`).join(' · ')
                : 'sem comparação com 2025 para este período'}
              hint={sem.length > 0
                ? `unidades, ${dataBr(semPer.inicio)} a ${dataBr(semPer.fim)} de 2026 contra o mesmo período de 2025 (Shopping de Preços, estimado)`
                : `a exportação de 2025 cobre outro período; unidades de ${dataBr(semPer.inicio)} a ${dataBr(semPer.fim)} de 2026: ${num(Object.values(p.mercado_semana ?? {}).reduce((t, x) => t + x.un26, 0))} (Shopping de Preços, estimado)`} />
          )}
          <KpiCard label="Próximas chegadas" value={p.chegadas.map((c) => dataBr(c.data)).join(' · ') || '—'}
            hint={`${p.chegadas.map((c) => `${c.embarque.slice(6)}: ${num(c.pecas)} pç`).join(' · ')}${p.chegadas.length ? ' · datas não confirmadas' : ''}`} />
          <KpiCard label="Meta da temporada" value={brl(metaTotal)}
            hint={Object.entries(p.meta_rs_categoria).map(([k, v]) => `${CATEGORIAS[k] ?? k} ${pct(metaTotal ? v / metaTotal : null)}`).join(' · ')} />
        </Stack>
      </CardContent></Card>

      <Secao titulo="Top 5 do dia" sub="Maior estoque a custo em jogo primeiro. Cada linha diz o problema, a ação sugerida e onde agir. Você aprova; nada muda sozinho.">
        {!b.top5.length
          ? <Typography variant="body2" color="text.secondary">Nenhum ponto de atenção hoje.</Typography>
          : (
            <Stack component="ol" spacing={1} sx={{ m: 0, pl: 3 }}>
              {b.top5.map((t) => (
                <Box component="li" key={t.sku}>
                  <Stack direction="row" spacing={1.5} alignItems="center" justifyContent="space-between">
                    <Box><Typography variant="body2"><b>{t.sku}</b> · {t.acao}</Typography>
                      <Typography variant="caption" color="text.secondary">{t.problema}</Typography></Box>
                    {t.aba !== 'Briefing' && <Button size="small" variant="outlined" sx={{ flexShrink: 0 }} onClick={() => onAbrirAba(t.aba)}>Abrir {t.aba}</Button>}
                  </Stack>
                </Box>
              ))}
            </Stack>
          )}
      </Secao>

      <Stack direction={{ xs: 'column', lg: 'row' }} spacing={2.5} alignItems="stretch">
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Secao titulo="O que mudou desde ontem" sub={b.mudancas.sem_comparacao ? 'Compara com a foto das 08:00 do dia anterior' : 'Comparado com a foto das 08:00 de ontem'}>
            {b.mudancas.sem_comparacao
              ? <Typography variant="body2" color="text.secondary">Primeira foto: sem comparação.</Typography>
              : (
                <Stack spacing={1.5}>
                  {GRUPOS_MUDANCA.map(([tipo, nome]) => {
                    const it = b.mudancas.itens.filter((m) => m.tipo === tipo)
                    if (!it.length) return null
                    const todos = abertos.has(`mud|${tipo}`)
                    return (
                      <Box key={tipo}>
                        <Typography variant="subtitle2">{nome} ({it.length})</Typography>
                        {(todos ? it : it.slice(0, 8)).map((m, i) => (
                          <Typography key={i} variant="body2" sx={{ py: 0.25 }}>
                            {m.sku && <b>{m.sku} </b>}{m.texto}
                            {m.id && m.loja?.startsWith('ml') && <>{' '}<Ext href={linkMl(m.id)}>{m.id}</Ext></>}
                          </Typography>
                        ))}
                        {it.length > 8 && <Button size="small" onClick={() => alternar(`mud|${tipo}`)}>{todos ? 'mostrar menos' : `+ ${it.length - 8} itens`}</Button>}
                      </Box>
                    )
                  })}
                  {!b.mudancas.itens.length && <Typography variant="body2" color="text.secondary">Nada mudou desde ontem.</Typography>}
                </Stack>
              )}
          </Secao>
        </Box>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Secao titulo="Alertas e dados" sub="Regras do painel, cupons e saúde das fontes">
            <Stack spacing={1}>
              {b.alertas.map((a, i) => <Alert key={`a${i}`} severity={a.nivel === 'err' ? 'error' : 'warning'} variant="outlined" sx={{ py: 0 }}>{a.texto}</Alert>)}
              {b.avisos.map((a, i) => <Alert key={`v${i}`} severity="info" variant="outlined" sx={{ py: 0 }}><b>Fonte:</b> {a}</Alert>)}
              {!b.alertas.length && !b.avisos.length && <Typography variant="body2" color="text.secondary">Nenhum alerta hoje.</Typography>}
            </Stack>
          </Secao>
        </Box>
      </Stack>

      <Secao titulo="Preço × mercado por SKU"
        sub="Uma linha por SKU com anúncio unitário, maior estoque a custo primeiro. Nosso preço = o mais barato de cada loja já com o melhor cupom do anúncio. Clique na linha para ver os anúncios."
        direita={<Chip size="small" variant="outlined" label="Preço e cupom: API" />}>
        {!b.decisao.length
          ? <Typography variant="body2" color="text.secondary" sx={{ py: 2, textAlign: 'center' }}>Nenhum SKU com anúncio unitário ativo hoje.</Typography>
          : (
            <TableContainer sx={{ border: 1, borderColor: 'divider', borderRadius: 1, maxHeight: '70vh' }}>
              <Table size="small" stickyHeader>
                <TableHead><TableRow>
                  {['SKU', 'Estoque', 'Vendas 7d', 'ML', 'Shopee Matriz', 'Shopee Decora', 'Shein', 'Mercado 2026 (3 fontes)', 'Posição', 'MC no nosso preço', 'MC na mediana', 'Leitura'].map((h, i) => (
                    <TableCell key={h} align={[1, 3, 4, 5, 6, 9, 10].includes(i) ? 'right' : 'left'} sx={{ whiteSpace: 'nowrap', fontWeight: 600 }}>{h}</TableCell>
                  ))}
                </TableRow></TableHead>
                <TableBody>
                  {b.decisao.map((r) => <LinhaSku key={r.sku} r={r} aberto={abertos.has(r.sku)} onAlternar={() => alternar(r.sku)} />)}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap sx={{ mt: 1.5 }}>
          <Chip size="small" color="error" label="▲ acima" /><Typography variant="caption">acima do p75 do mercado</Typography>
          <Chip size="small" color="success" label="● na faixa" /><Typography variant="caption">entre p25 e p75</Typography>
          <Chip size="small" color="info" label="▼ abaixo" /><Typography variant="caption">abaixo do p25</Typography>
          <Chip size="small" variant="outlined" label="referência fraca" /><Typography variant="caption">recorte de mercado pouco confiável</Typography>
          <Typography variant="caption" color="text.secondary">* com cupom · ¹ sem leitura de cupom (Shein)</Typography>
        </Stack>
        <Stack spacing={0.75} sx={{ mt: 1.5, pt: 1.5, borderTop: 1, borderColor: 'divider' }}>
          {rodapeMercado(b).map(([h, t]) => <Typography key={h} variant="caption" color="text.secondary"><b>{h}:</b> {t}</Typography>)}
        </Stack>
      </Secao>

      <Secao titulo="Próximo container: prontidão"
        sub={b.prontidao.length ? `${num(b.prontidao.reduce((a, x) => a + x.pecas, 0))} peças a chegar nos próximos dias, ${b.prontidao.length} SKUs · datas não confirmadas` : undefined}>
        {!b.prontidao.length
          ? <Typography variant="body2" color="text.secondary">Nenhum lote não confirmado chega nos próximos 14 dias.</Typography>
          : (
            <TableContainer sx={{ border: 1, borderColor: 'divider', borderRadius: 1, maxHeight: '60vh' }}>
              <Table size="small" stickyHeader>
                <TableHead><TableRow>
                  {['SKU', 'Embarque', 'Peças', 'Estoque a custo', 'Tiny', 'CMV lote novo', 'ML Matriz', 'Shopee Matriz', 'Shein', 'Pendência'].map((h, i) => (
                    <TableCell key={h} align={i === 2 || i === 3 ? 'right' : i >= 4 && i <= 8 ? 'center' : 'left'} sx={{ whiteSpace: 'nowrap', fontWeight: 600 }}>{h}</TableCell>
                  ))}
                </TableRow></TableHead>
                <TableBody>
                  {b.prontidao.map((x) => (
                    <TableRow key={x.sku} hover>
                      <TableCell><b>{x.sku}</b><Typography variant="caption" color="text.secondary" display="block" sx={{ maxWidth: 240 }} noWrap title={x.desc}>{x.desc}</Typography></TableCell>
                      <TableCell sx={{ whiteSpace: 'nowrap' }}>{x.embarque} · {dataBr(x.data)}</TableCell>
                      <TableCell align="right">{num(x.pecas)}</TableCell>
                      <TableCell align="right">{brl(x.custo_rs)}</TableCell>
                      {[x.tiny, x.cmv].map((ok, i) => <TableCell key={i} align="center"><Chip size="small" color={ok ? 'success' : 'error'} label={ok ? 'ok' : 'falta'} /></TableCell>)}
                      {['ml_matriz', 'shopee_loja1', 'shein'].map((l) => {
                        const s = x.lojas[l] ? SELO[x.lojas[l] as string] : null
                        return <TableCell key={l} align="center">{s ? <Chip size="small" color={s[1]} variant={s[1] === 'default' ? 'outlined' : 'filled'} label={s[0]} /> : <Chip size="small" variant="outlined" label="sem leitura" />}</TableCell>
                      })}
                      <TableCell><Typography variant="caption">{x.pendencias}</Typography></TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
      </Secao>

      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', lg: 'repeat(2, minmax(0, 1fr))' }, gap: 2.5 }}>
        <Secao titulo="Estoque parado sem anúncio" sub="Tem estoque e falta anúncio em loja com 5% ou mais da meta">
          {!o.parado.length && <Typography variant="body2" color="text.secondary">Nenhum SKU parado hoje.</Typography>}
          {o.parado.map((x) => (
            <ItemLista key={x.sku}
              topo={<><b>{x.sku}</b> · {num(x.estoque)} pç ({brl(x.estoque_rs)} a custo) {x.nenhuma && <Chip size="small" color="error" label="nenhuma loja" />}</>}
              base={`Falta em: ${x.lojas.map((l) => `${nomeLoja(l.loja)} (${l.selo === 'pausado' ? 'pausado' : 'sem anúncio'}, ${pct(l.part)} da meta)`).join(' · ')}`} />
          ))}
        </Secao>
        <Secao titulo="Catálogos nossos invadidos"
          sub="Catálogo de marca nossa com vendedor que não é ML Matriz nem ML Extrema. Fonte: API do ML, leitura de catálogos do painel.">
          {o.invadidos.length === 0
            ? <ItemLista topo={<><Chip size="small" color="success" label="nenhum hoje" /> {o.nossos_cat} catálogos de marca nossa monitorados. Só vendem neles ML Matriz e ML Extrema.</>} />
            : o.invadidos.map((v) => (
              <ItemLista key={v.id} topo={<><b>{v.sku}</b> · <Ext href={linkCatalogoMl(v.catalogo)}>{v.marca || v.catalogo}</Ext>{' '}
                <Chip size="small" color="error" label={`${v.fora.length} vendedor(es) de fora`} /></>} />
            ))}
          {o.duplicados.length > 0 && (
            <ItemLista topo={<><Chip size="small" color="warning" label="atenção" /> Em {o.duplicados.length} catálogos há 2 anúncios nossos no mesmo catálogo ({o.duplicados.join(', ')}): dividem as visitas entre si.</>} />
          )}
        </Secao>
        <Secao titulo="Catálogos de terceiros em que estamos perdendo" sub="Entramos no catálogo de outra marca e não somos o vencedor. Fonte: API do ML.">
          {!o.disputa.length && <Typography variant="body2" color="text.secondary">Nenhum catálogo de terceiro em disputa.</Typography>}
          {o.disputa.map((v) => (
            <ItemLista key={v.id} topo={<><b>{v.sku}</b> · <Ext href={linkCatalogoMl(v.catalogo)}>{v.marca || v.catalogo}</Ext></>}
              base={`Vencedor ${v.vencedor_nome ?? '—'}${v.vencedor_full ? ' (Full)' : ''} · nosso ${brl2(v.preco)} · para ganhar ${brl2(v.p2w)} · ${(v.veredito && VEREDITO[v.veredito]) || v.veredito || ''}`} />
          ))}
        </Secao>
        <Secao titulo="Catálogos de terceiros para entrar" sub="Aderência alta, com vendedor ativo e maior MC no alvo (rotina das 06:00; sem demanda: o ML não dá visita de catálogo de terceiro)">
          {!o.terceiros.length && <Typography variant="body2" color="text.secondary">Nenhum candidato hoje.</Typography>}
          {o.terceiros.map((c) => (
            <ItemLista key={c.link} topo={<><b>{c.sku}</b> · <Ext href={c.link}>{(c.nome || c.link).slice(0, 60)}</Ext>{c.novo && <> <Chip size="small" color="info" label="novo" /></>}</>}
              base={`${c.vendedores != null ? `${num(c.vendedores)} vendedor(es) · ` : ''}preço-alvo ${brl2(c.preco_alvo)} · MC ${pct(c.mc_alvo, 1)} · aderência ${c.aderencia || '—'}${c.risco_inpi ? ` · INPI ${c.risco_inpi}` : ''}`} />
          ))}
        </Secao>
      </Box>

      <EnvioShopping onMsg={onMsg} />
      <LinksConcorrentes onMsg={onMsg} />
      <Conversa />

      <Typography variant="caption" color="text.secondary">
        Fontes: payload do painel{b.gerado_em ? ` montado em ${b.gerado_em}` : ''} (vendas e MC = margens por pedido; estoque = Tiny Galpão Cabreúva e Full pelas APIs; anúncios pelas APIs ML, Shopee e Shein);
        cupons lidos pelas APIs na montagem; Shopping de Preços e base 2025 para o mercado. Confirmado = vendas, estoque, preços, cupons. Estimado = mercado 2026 (faixas do ML).
      </Typography>
    </Stack>
  )
}
